import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { tanimlarApi } from '../services/api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const YETKI_RENK: Record<string, string> = {
  goruntule: '#3498db', ekle: '#27ae60', duzenle: '#e67e22',
  sil: '#e74c3c', onayla: '#9b59b6', rapor: '#1abc9c'
};

const YETKI_AD: Record<string, string> = {
  goruntule: '👁️ Görüntüle', ekle: '➕ Ekle', duzenle: '✏️ Düzenle',
  sil: '🗑️ Sil', onayla: '✅ Onayla', rapor: '📊 Rapor'
};

const ROL_RENKLER: Record<string, string> = {
  super_admin: '#1a5276', gm_yonetici: '#1f618d',
  gm_personel: '#2874a6', isletme_mudur: '#1e8449', isletme_personel: '#27ae60'
};

const ROL_AD: Record<string, string> = {
  super_admin: 'Süper Yönetici', gm_yonetici: 'GM Yöneticisi',
  gm_personel: 'GM Personeli', isletme_mudur: 'İşletme Müdürü', isletme_personel: 'İşletme Personeli'
};

export default function YetkilendirmeYonetimi() {
  const [kullanicilar, setKullanicilar] = useState<any[]>([]);
  const [menuTanimlari, setMenuTanimlari] = useState<any[]>([]);
  const [yetkiSeviyeleri, setYetkiSeviyeleri] = useState<any[]>([]);
  const [seciliKullanici, setSeciliKullanici] = useState<any>(null);
  const [kullaniciYetkiler, setKullaniciYetkiler] = useState<Record<string, string[]>>({});
  const [mesaj, setMesaj] = useState('');
  const [degisti, setDegisti] = useState(false);
  const [aramaMetni, setAramaMetni] = useState('');
  const [rolFiltre, setRolFiltre] = useState('');
  const [kapaliGruplar, setKapaliGruplar] = useState<Set<string>>(new Set());

  useEffect(() => {
    tanimlarApi.kullaniciListesi().then(r => setKullanicilar(r.data));
    axios.get(`${API}/api/yetkiler/menu-tanimlari`, { headers: headers() })
      .then(r => setMenuTanimlari(r.data));
    axios.get(`${API}/api/yetkiler/yetki-seviyeleri`, { headers: headers() })
      .then(r => setYetkiSeviyeleri(r.data));
  }, []);

  const filtreliKullanicilar = useMemo(() =>
    kullanicilar.filter(k => {
      const aramaUyumu = !aramaMetni ||
        k.ad_soyad.toLowerCase().includes(aramaMetni.toLowerCase()) ||
        k.kullanici_adi.toLowerCase().includes(aramaMetni.toLowerCase());
      return aramaUyumu && (!rolFiltre || k.rol === rolFiltre);
    }),
    [kullanicilar, aramaMetni, rolFiltre]
  );

  const gruplar = useMemo(() => {
    const g: Record<string, any[]> = {};
    menuTanimlari.forEach(m => {
      if (!g[m.grup]) g[m.grup] = [];
      g[m.grup].push(m);
    });
    return g;
  }, [menuTanimlari]);

  const kullaniciSec = (k: any) => {
    setSeciliKullanici(k);
    setDegisti(false);
    axios.get(`${API}/api/yetkiler/kullanici/${k.id}`, { headers: headers() })
      .then(r => setKullaniciYetkiler(r.data))
      .catch(() => setKullaniciYetkiler({}));
  };

  const rolUygula = async (rol: string) => {
    if (!seciliKullanici) return;
    await axios.post(
      `${API}/api/yetkiler/rol-uygula?kullanici_id=${seciliKullanici.id}&rol=${rol}`,
      {}, { headers: headers() }
    );
    kullaniciSec(seciliKullanici);
    setMesaj(`✅ ${ROL_AD[rol] || rol} rol yetkileri uygulandı`);
    setTimeout(() => setMesaj(''), 3000);
  };

  const toggleYetki = (menuId: string, yetki: string) => {
    setKullaniciYetkiler(prev => {
      const mevcutlar = prev[menuId] || [];
      const yeni = mevcutlar.includes(yetki)
        ? mevcutlar.filter(y => y !== yetki)
        : [...mevcutlar, yetki];
      return { ...prev, [menuId]: yeni };
    });
    setDegisti(true);
  };

  const tumMenuToggle = (menuId: string) => {
    const tumYetkiler = yetkiSeviyeleri.map((y: any) => y.id);
    setKullaniciYetkiler(prev => {
      const mevcutlar = prev[menuId] || [];
      return { ...prev, [menuId]: mevcutlar.length === tumYetkiler.length ? [] : tumYetkiler };
    });
    setDegisti(true);
  };

  // Sütun başlığına tıkla → o yetki tipini tüm modüller için aç/kapat
  const sutunToggle = (yetkiId: string) => {
    const tumMenuIds = menuTanimlari.map(m => m.id);
    const hepsiAcik = tumMenuIds.every(mid => (kullaniciYetkiler[mid] || []).includes(yetkiId));
    setKullaniciYetkiler(prev => {
      const yeni = { ...prev };
      tumMenuIds.forEach(mid => {
        const mevcut = yeni[mid] || [];
        if (hepsiAcik) {
          yeni[mid] = mevcut.filter(y => y !== yetkiId);
        } else {
          if (!mevcut.includes(yetkiId)) yeni[mid] = [...mevcut, yetkiId];
        }
      });
      return yeni;
    });
    setDegisti(true);
  };

  // Gruptaki tüm modüller × tüm yetkiler toggle
  const grupTumToggle = (grup: string) => {
    const grupMenuIds = (gruplar[grup] || []).map(m => m.id);
    const tumYetkiler = yetkiSeviyeleri.map((y: any) => y.id);
    const hepsiTam = grupMenuIds.every(mid =>
      tumYetkiler.every(y => (kullaniciYetkiler[mid] || []).includes(y))
    );
    setKullaniciYetkiler(prev => {
      const yeni = { ...prev };
      grupMenuIds.forEach(mid => { yeni[mid] = hepsiTam ? [] : [...tumYetkiler]; });
      return yeni;
    });
    setDegisti(true);
  };

  const toggleGrup = (grup: string) => {
    setKapaliGruplar(prev => {
      const yeni = new Set(prev);
      yeni.has(grup) ? yeni.delete(grup) : yeni.add(grup);
      return yeni;
    });
  };

  const kaydet = async () => {
    if (!seciliKullanici) return;
    const yetkiler = Object.entries(kullaniciYetkiler)
      .filter(([, v]) => v.length > 0)
      .map(([menu_id, yetki_tipleri]) => ({ menu_id, yetki_tipleri }));
    await axios.post(
      `${API}/api/yetkiler/toplu-guncelle`,
      { kullanici_id: seciliKullanici.id, yetkiler },
      { headers: headers() }
    );
    setDegisti(false);
    setMesaj('✅ Yetkiler kaydedildi');
    setTimeout(() => setMesaj(''), 3000);
  };

  const aktifModulSayisi = useMemo(
    () => Object.values(kullaniciYetkiler).filter(v => v.length > 0).length,
    [kullaniciYetkiler]
  );

  const hepsiniAc = () => setKapaliGruplar(new Set());
  const hepsiniKapat = () => setKapaliGruplar(new Set(Object.keys(gruplar)));

  return (
    <div>
      <div className="page-header">
        <h1>🔐 Yetkilendirme Yönetimi</h1>
        <p>Kullanıcılara menü erişimi ve işlem yetkileri verin</p>
      </div>

      {mesaj && (
        <div style={{ background: '#d5f5e3', color: '#1e8449', padding: '10px 14px', borderRadius: 4, marginBottom: 14, fontSize: 14 }}>
          {mesaj}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12 }}>

        {/* Sol - Kullanıcı Listesi */}
        <div style={{ width: 270, background: 'white', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', flexShrink: 0, alignSelf: 'flex-start' }}>
          <div style={{ background: '#1a5276', color: 'white', padding: '10px 14px', fontSize: 14, fontWeight: 'bold' }}>
            👥 Kullanıcılar
          </div>

          {/* Arama + Filtre */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #eee', background: '#f8f9fa', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              type="text"
              placeholder="İsim veya kullanıcı ara..."
              value={aramaMetni}
              onChange={e => setAramaMetni(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12, width: '100%', boxSizing: 'border-box' }}
            />
            <select
              value={rolFiltre}
              onChange={e => setRolFiltre(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12, background: 'white', width: '100%' }}
            >
              <option value="">— Tüm Roller —</option>
              {Object.entries(ROL_AD).map(([rol, ad]) => (
                <option key={rol} value={rol}>{ad}</option>
              ))}
            </select>
          </div>

          {/* Liste */}
          <div style={{ maxHeight: 520, overflowY: 'auto' }}>
            {filtreliKullanicilar.length === 0 && (
              <div style={{ padding: 20, color: '#aaa', fontSize: 13, textAlign: 'center' }}>Kullanıcı bulunamadı</div>
            )}
            {filtreliKullanicilar.map(k => (
              <div key={k.id} onClick={() => kullaniciSec(k)}
                style={{
                  padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                  background: seciliKullanici?.id === k.id ? '#eaf4fb' : 'white',
                  borderLeft: `4px solid ${seciliKullanici?.id === k.id ? '#1a5276' : 'transparent'}`
                }}>
                <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1a5276' }}>{k.ad_soyad}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{k.kullanici_adi}</div>
                <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, background: ROL_RENKLER[k.rol] || '#aaa', color: 'white', padding: '1px 6px', borderRadius: 3 }}>
                    {ROL_AD[k.rol] || k.rol}
                  </span>
                  {seciliKullanici?.id === k.id && aktifModulSayisi > 0 && (
                    <span style={{ fontSize: 10, background: '#eaf4fb', color: '#1a5276', padding: '1px 5px', borderRadius: 3, border: '1px solid #b5d5ec' }}>
                      {aktifModulSayisi} modül
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Filtre özeti */}
          {(aramaMetni || rolFiltre) && (
            <div style={{ padding: '6px 10px', background: '#f0f4f8', borderTop: '1px solid #eee', fontSize: 11, color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{filtreliKullanicilar.length} / {kullanicilar.length} kullanıcı</span>
              <span
                onClick={() => { setAramaMetni(''); setRolFiltre(''); }}
                style={{ cursor: 'pointer', color: '#e74c3c' }}
              >✕ Temizle</span>
            </div>
          )}
        </div>

        {/* Sağ - Yetki Tablosu */}
        {seciliKullanici ? (
          <div style={{ flex: 1, background: 'white', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

            {/* Başlık */}
            <div style={{ background: '#1f618d', color: 'white', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>{seciliKullanici.ad_soyad}</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>{seciliKullanici.kullanici_adi} • {ROL_AD[seciliKullanici.rol]}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {degisti && (
                  <span style={{ fontSize: 12, background: '#e67e22', padding: '3px 8px', borderRadius: 3 }}>
                    ● Kaydedilmemiş değişiklik
                  </span>
                )}
                <button className="btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontSize: 13 }} onClick={kaydet}>
                  💾 Kaydet
                </button>
              </div>
            </div>

            {/* Hızlı Rol Uygula + Grup kontrolleri */}
            <div style={{ padding: '8px 16px', background: '#f8f9fa', borderBottom: '1px solid #eee', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#666' }}>Hızlı Uygula:</span>
              {Object.entries(ROL_AD).map(([rol, ad]) => (
                <button key={rol} onClick={() => rolUygula(rol)}
                  style={{ background: ROL_RENKLER[rol] || '#aaa', color: 'white', border: 'none', padding: '3px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                  {ad}
                </button>
              ))}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button onClick={hepsiniAc} style={{ fontSize: 11, padding: '3px 8px', background: '#eee', border: '1px solid #ddd', borderRadius: 3, cursor: 'pointer' }}>▼ Tümünü Aç</button>
                <button onClick={hepsiniKapat} style={{ fontSize: 11, padding: '3px 8px', background: '#eee', border: '1px solid #ddd', borderRadius: 3, cursor: 'pointer' }}>▶ Tümünü Kapat</button>
              </div>
            </div>

            {/* Yetki Tablosu */}
            <div style={{ overflow: 'auto', padding: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#eaf0fb' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 13, width: '35%' }}>Menü / Modül</th>
                    {yetkiSeviyeleri.map((y: any) => (
                      <th key={y.id} style={{ padding: '8px 6px', textAlign: 'center', fontSize: 12 }}>
                        <div
                          onClick={() => sutunToggle(y.id)}
                          title={`Tüm satırlarda "${YETKI_AD[y.id]}" yetkisini aç/kapat`}
                          style={{
                            background: YETKI_RENK[y.id], color: 'white', padding: '3px 6px',
                            borderRadius: 4, whiteSpace: 'nowrap', cursor: 'pointer',
                            userSelect: 'none', fontSize: 11
                          }}>
                          {YETKI_AD[y.id]}
                        </div>
                      </th>
                    ))}
                    <th style={{ padding: '8px 6px', textAlign: 'center', fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>Tümü</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(gruplar).map(([grup, menuler]) => {
                    const kapali = kapaliGruplar.has(grup);
                    const grupMenuIds = menuler.map(m => m.id);
                    const tumYetkiler = yetkiSeviyeleri.map((y: any) => y.id);
                    const hepsiTam = grupMenuIds.every(mid =>
                      tumYetkiler.every(y => (kullaniciYetkiler[mid] || []).includes(y))
                    );
                    const hicbiri = grupMenuIds.every(mid => !(kullaniciYetkiler[mid]?.length));
                    return (
                      <React.Fragment key={grup}>
                        {/* Grup başlık satırı — tıklayınca katlanır */}
                        <tr style={{ background: '#e8eef5', cursor: 'pointer' }} onClick={() => toggleGrup(grup)}>
                          <td style={{ padding: '7px 14px', fontSize: 12, fontWeight: 'bold', color: '#1a5276', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                            <span style={{ marginRight: 6, fontSize: 10 }}>{kapali ? '▶' : '▼'}</span>
                            {grup}
                            <span style={{ marginLeft: 8, fontWeight: 'normal', color: '#888', fontSize: 11 }}>({menuler.length} modül)</span>
                          </td>
                          <td colSpan={yetkiSeviyeleri.length} />
                          {/* Grup toplu toggle butonu */}
                          <td style={{ padding: '7px 6px', textAlign: 'center' }}>
                            <div
                              onClick={e => { e.stopPropagation(); grupTumToggle(grup); }}
                              title="Gruptaki tüm modül yetkilerini aç/kapat"
                              style={{
                                width: 24, height: 24, borderRadius: 4, cursor: 'pointer', margin: '0 auto',
                                background: hepsiTam ? '#1a5276' : hicbiri ? '#f0f0f0' : '#8ab4d4',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: (hepsiTam || !hicbiri) ? 'white' : '#ccc',
                                fontSize: 13, border: '2px solid #ccc'
                              }}>
                              {hepsiTam ? '✓' : hicbiri ? '' : '−'}
                            </div>
                          </td>
                        </tr>

                        {/* Modül satırları */}
                        {!kapali && menuler.map(menu => (
                          <tr key={menu.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '8px 14px 8px 28px', fontSize: 13 }}>
                              {menu.ikon} {menu.ad}
                            </td>
                            {yetkiSeviyeleri.map((y: any) => {
                              const var_ = (kullaniciYetkiler[menu.id] || []).includes(y.id);
                              return (
                                <td key={y.id} style={{ padding: '6px', textAlign: 'center' }}>
                                  <div
                                    onClick={() => toggleYetki(menu.id, y.id)}
                                    style={{
                                      width: 26, height: 26, borderRadius: 4, cursor: 'pointer', margin: '0 auto',
                                      background: var_ ? YETKI_RENK[y.id] : '#f0f0f0',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      color: var_ ? 'white' : '#ccc', fontSize: 13,
                                      border: `2px solid ${var_ ? YETKI_RENK[y.id] : '#ddd'}`
                                    }}>
                                    {var_ ? '✓' : ''}
                                  </div>
                                </td>
                              );
                            })}
                            <td style={{ padding: '6px', textAlign: 'center' }}>
                              <div
                                onClick={() => tumMenuToggle(menu.id)}
                                style={{
                                  width: 26, height: 26, borderRadius: 4, cursor: 'pointer', margin: '0 auto',
                                  background: (kullaniciYetkiler[menu.id] || []).length === yetkiSeviyeleri.length ? '#1a5276' : '#f0f0f0',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  color: (kullaniciYetkiler[menu.id] || []).length === yetkiSeviyeleri.length ? 'white' : '#ccc',
                                  fontSize: 13, border: '2px solid #ddd'
                                }}>
                                {(kullaniciYetkiler[menu.id] || []).length === yetkiSeviyeleri.length ? '✓' : ''}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Alt kaydet */}
            <div style={{ padding: 16, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => kullaniciSec(seciliKullanici)} style={{ background: '#eee' }}>İptal</button>
              <button className="btn btn-primary" onClick={kaydet} style={{ fontSize: 14 }}>💾 Yetkileri Kaydet</button>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, background: 'white', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔐</div>
              <div style={{ fontSize: 15 }}>Soldan bir kullanıcı seçin</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
